import React from 'react';
import { MdAccountCircle, MdVerified, MdWarning, MdLockClock } from 'react-icons/md';

const DriverLicense = ({ data }) => {
  console.log("DriverLicense > data :", data);
  if (!data?.driving_record || !data?.driving_record?.license) {
    return (
      <div className="w-full border border-gray-200 rounded-lg p-6 bg-white/50 shadow-sm animate-pulse">
        <div className="flex space-x-4 items-center">
          <div className="rounded-full bg-gray-200 h-12 w-12"></div>
          <div className="flex-1 space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  const record = data.driving_record;
  const info = record.query_information;
  const license = record.license;
  // const driver = record.driver["address"];
  const driver = record.driver;
  const order = record.order;

  // Status data
  const statusMap = {
    valid: { color: "text-green-600", bgColor: "bg-green-50", icon: <MdVerified className="text-green-500" /> },
    suspended: { color: "text-amber-600", bgColor: "bg-amber-50", icon: <MdWarning className="text-amber-500" /> },
    expired: { color: "text-red-600", bgColor: "bg-red-50", icon: <MdLockClock className="text-red-500" /> },
    default: { color: "text-gray-600", bgColor: "bg-gray-50", icon: <MdLockClock className="text-gray-500" /> }
  };
  
  const statusKey = license.status?.toLowerCase() || "default";
  const statusData = statusMap[statusKey] || statusMap.default;
  
  // Check if license is not found (error condition)
  const isNotFound = order && order.success === false && order.error_message;
  
  // Calculate if license is about to expire (within 90 days)
  const isAboutToExpire = () => {
    if (!license.expiration_date) return false;
    const expDate = new Date(license.expiration_date);
    const today = new Date();
    const diffTime = expDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 && diffDays <= 90;
  };

  return (
    <div className="w-full">
      <div className="border border-gray-200 rounded-lg p-3 bg-gradient-to-br from-white to-gray-50/30">
        {/* Top Row - License Header */}
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-semibold">
              {license.type || "NON COMMERCIAL"}
            </div>
            <div className={`px-2 py-1 rounded text-xs font-medium ${statusData.bgColor} ${statusData.color}`}>
              {license.status || "VALID"}
            </div>
          </div>
          <div className="text-xs text-gray-500">
            Report Date: {order?.report_date || 'N/A'}
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-12 gap-4">
          {/* Driver Info - 5 cols */}
          <div className="col-span-5 flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-lg font-bold shadow-sm">
              {/* {info.first_name[0]}{info.last_name[0]} */}
              {driver.first_name[0]}{driver.last_name[0]}
            </div>
            <div className="min-w-0">
              <div className="font-medium text-gray-900 truncate">
                {driver.first_name} {driver.last_name}
              </div>
              <div className="text-xs text-gray-500 truncate">{driver.full_address}</div>
              <div className="text-xs text-gray-400 truncate">
                {info?.city || driver.city}, {info?.state || license?.state} {info?.zip || driver.zip}
              </div>
            </div>
          </div>

          {/* License Details - 4 cols */}
          <div className="col-span-4 grid grid-cols-2 gap-2">
            <div className="bg-gray-50 px-2 py-1.5 rounded border border-gray-100">
              <span className="text-xs text-gray-500 block">DOB</span>
              <span className="text-xs font-medium">{info.date_of_birth}</span>
            </div>
            <div className="bg-gray-50 px-2 py-1.5 rounded border border-gray-100">
              <span className="text-xs text-gray-500 block">Height</span>
              <span className="text-xs font-medium">{info.height || 'N/A'}</span>
            </div>
            <div className="bg-blue-50 px-2 py-1.5 rounded border border-blue-100">
              <span className="text-xs text-blue-500 block">State</span>
              <span className="text-xs font-semibold text-blue-700">{license.state}</span>
            </div>
            <div className="bg-purple-50 px-2 py-1.5 rounded border border-purple-100">
              <span className="text-xs text-purple-500 block">Class</span>
              <span className="text-xs font-semibold text-purple-700">{license.class || "D"}</span>
            </div>
          </div>

          {/* License Status - 3 cols */}
          <div className="col-span-3 flex flex-col gap-2">
            <div className="bg-gray-50 px-2 py-1.5 rounded border border-gray-100">
              <span className="text-xs text-gray-500 block">License #</span>
              <span className="text-xs font-mono font-medium">{license.license_number}</span>
            </div>
            <div className="flex gap-2">
              <div className="flex-1 bg-green-50 px-2 py-1 rounded border border-green-100">
                <span className="text-[10px] text-green-600 block">Issued</span>
                <span className="text-xs font-medium text-green-700">{license.issue_date}</span>
              </div>
              <div className={`flex-1 px-2 py-1 rounded border ${
                isAboutToExpire()
                  ? 'bg-amber-50 border-amber-100'
                  : 'bg-gray-50 border-gray-100'
              }`}>
                <span className={`text-[10px] block ${
                  isAboutToExpire() ? 'text-amber-600' : 'text-gray-500'
                }`}>Expires</span>
                <span className={`text-xs font-medium ${
                  isAboutToExpire() ? 'text-amber-700' : 'text-gray-700'
                }`}>{license.expiration_date}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Restrictions & Endorsements */}
        {(license.restrictions || license.endorsements) && (
          <div className="mt-2 pt-2 border-t border-gray-100 flex gap-4">
            {license.restrictions && (
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-red-500 font-medium">RESTR:</span>
                <span className="text-xs text-gray-600">{license.restrictions}</span>
              </div>
            )}
            {license.endorsements && (
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-green-500 font-medium">END:</span>
                <span className="text-xs text-gray-600">{license.endorsements}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverLicense;